import type { DateWithItinerary } from '@apps/main/modules/state/tripState';
import {
  getFormattedDateMonth,
  money,
} from '@apps/main/modules/helpers/formatting';

import { assertValue } from '@libs/utils';
import { SemboDate, DateFormat } from '@libs/sembo-dates';
import type { AppSelectOption } from '@libs/ui';
import type { ItineraryWithPrice } from '@libs/trip-planner-interfaces';

import { PriceRibbonDayTileType } from '../../../modules/priceRibbon';
import type {
  PriceRibbonDay,
  PriceRibbonDayTile,
  PriceRibbonMonth,
} from '../../../modules/priceRibbon';

export function getMonthsSelectOptions(
  months: Date[]
): AppSelectOption<string>[] {
  return months.map(
    (month: Date): AppSelectOption<string> => ({
      id: formatDate(month, DateFormat.ISO),
      label: `${formatDate(month, DateFormat.MonthFullName)} ${formatDate(
        month,
        DateFormat.FullYear
      )}`,
    })
  );
}

// TODO: test
export function getSelectedMonthOption(
  monthsSelectOptions: AppSelectOption<string>[],
  isDateInExpandedMonth: (date: Date) => boolean
): AppSelectOption<string> {
  const monthsSelectedOption = monthsSelectOptions[0];

  assertValue(
    monthsSelectedOption,
    'priceCalendarLogic.ts > getSelectedMonthOption > monthsSelectedOption'
  );

  return (
    monthsSelectOptions.find((option: AppSelectOption<string>) =>
      isDateInExpandedMonth(new Date(option.id))
    ) ?? monthsSelectedOption
  );
}

export function getPriceRibbonMonths(
  months: Date[],
  itineraryDates: DateWithItinerary[],
  isLoading: boolean,
  weekdays: string[],
  isSelectedItinerary: (date: Date) => boolean,
  isDateInExpandedMonth: (date: Date) => boolean,
  selectedItinerary: ItineraryWithPrice | undefined
): PriceRibbonMonth[] {
  function mapItineraryDatesToPriceCalendarDays(
    dateWithItinerary: DateWithItinerary[]
  ): PriceRibbonDay[] {
    function getPriceCalendarDayTile(
      date: DateWithItinerary
    ): PriceRibbonDayTile {
      if (date.itinerary?.totalPrice) {
        return {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: money(date.itinerary?.totalPrice),
        };
      } else if (date.itinerary?.totalPriceWithAlternative) {
        return {
          type: PriceRibbonDayTileType.AlternativePrice,
          priceWithCurrency: money(date.itinerary?.totalPriceWithAlternative),
        };
      } else if (isLoading && !date.itinerary) {
        return {
          type: PriceRibbonDayTileType.LoadingPrice,
        };
      } else {
        return {
          type: PriceRibbonDayTileType.NoAvailability,
        };
      }
    }

    return dateWithItinerary.map((date: DateWithItinerary): PriceRibbonDay => {
      const weekday = weekdays[new SemboDate(date.date).getDay()];

      const currentDate = new SemboDate(date.date);

      assertValue(
        weekday,
        `priceCalendarLogic.ts > getPriceRibbonMonths > weekday > ${new SemboDate(
          date.date
        ).getDay()}`
      );

      const isSuggested = selectedItinerary
        ? currentDate.format(DateFormat.Date) ===
          new SemboDate(selectedItinerary.date).format(DateFormat.Date)
        : false;

      return {
        date: currentDate.format(DateFormat.ISO),
        dayMonth: getFormattedDateMonth(date.date),
        weekday,
        isSelected: isSelectedItinerary(date.date),
        isSuggested: isSuggested && !isSelectedItinerary(date.date),
        state: getPriceCalendarDayTile(date),
      };
    });
  }

  return months.map((month: Date) => {
    const isExtended = isDateInExpandedMonth(new Date(month));

    return {
      month: formatDate(month, DateFormat.MonthFullName),
      monthShort: formatDate(month, DateFormat.MonthShortName),
      year: formatDate(month, DateFormat.FullYear),
      isExtended,
      firstDayInMonthISO: month.toDateString(),
      days: isExtended
        ? mapItineraryDatesToPriceCalendarDays(itineraryDates)
        : [],
    };
  });
}

function formatDate(month: Date, format: DateFormat): string {
  const date = new SemboDate(month).format(format);
  return date.at(0)?.toUpperCase() + date.slice(1);
}
